import { ClinicModel, UserModelBase } from 'core/models';
import { Clinic, RawClinic } from 'core/models/clinicModel';
import { UserBase } from 'core/models/userModelBase';
import {
  RawRole,
  Role,
  RoleModel,
  RolesCodeEnum,
  RolesEnum,
} from './roleModel';
import { SiteSimple } from './siteModel';

// eslint-disable-next-line no-shadow
export enum UserStatusEnum {
  unverified = 'unverified',
  enabled = 'enabled',
  disabled = 'disabled',
  active = 'active',
  inactiveNeverUsed = 'Inactive Never Used',
  activeNeverUsed = 'Active Never Used',
  inactive = 'Inactive',
  lastActive = 'Last Active',
}

// eslint-disable-next-line no-shadow
export enum ActiveEnum {
  enabled = 'enabled',
  disabled = 'disabled',
}

export interface ChangeProfileFormData {
  signatureAuthorize?: boolean;
  language?: string;
  isAudio?: boolean;
  mfaEnabled?: boolean;
}

export interface WebVersionInfo {
  readStatus: boolean;
  time: string;
  version: string;
  url: string;
}

export interface MFAItem {
  id: string;
  name: string;
  mfaType: string;
  createdAt: string;
  updatedAt: string;
}

export interface User extends UserBase {
  roles?: any;
  authMethod?: string;
  clinic: Clinic;
  mfaEnabled?: boolean;
  status: UserStatusEnum;
  phone?: string;
  position?: string;
  passwordUpdatedAt?: number;
  tenant: Clinic;
  mfaList?: MFAItem[];
  site?: string;
  siteId?: string;
  siteIds?: string[];
  siteName?: string;
  sites?: SiteSimple[];
  assignedPatient?: boolean;
  isVivaLNKAdmin?: boolean;
  isProgramManager?: boolean;
  isAdmin?: boolean;
  isPhysician?: boolean;
  isTechnician?: boolean;
  isResearcher?: boolean;
  language?: string;
  signature?: string;
  signatureAuthorize?: boolean;
  webVersionInfo?: WebVersionInfo;
  isAudio: boolean;
  createdAt?: number;
  lastLoggedAt?: number;
  roleNames?: string;
  active: string;
}

export interface RawUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles?: any;
  clinic: RawClinic;
  tenant: RawClinic;
  mfaList?: MFAItem[];
  mfaRequired?: boolean;
  authMethod?: string;
  mfaEnabled?: boolean;
  status: UserStatusEnum;
  passwordUpdatedAt?: number;
  phone?: string;
  position?: string;
  createdAt: number;
  updatedAt: string;
  siteId?: string;
  siteName?: string;
  sites?: SiteSimple[];
  assignedPatient?: boolean;
  language?: string;
  signature?: string;
  signatureAuthorize?: boolean;
  webVersionInfo?: WebVersionInfo;
  isAudio: boolean;
  lastLoggedAt?: number;

  roleNames?: string;
  active: string;
}

export interface UserRoleProps {
  userRole?: RolesCodeEnum;
  search?: string;
}

export class UserModel extends UserModelBase implements User {
  roles?: any;

  clinic: Clinic;

  tenant: Clinic;

  mfaList?: MFAItem[];

  status: UserStatusEnum = UserStatusEnum.disabled;

  phone?: string;

  position?: string;

  siteId?: string;

  siteName?: string;

  sites?: SiteSimple[];

  assignedPatient?: boolean;

  isVivaLNKAdmin?: boolean;

  isProgramManager?: boolean;

  isAdmin?: boolean;

  isPhysician?: boolean;

  isTechnician?: boolean;

  isResearcher?: boolean;

  language?: string;

  signature?: string;

  signatureAuthorize?: boolean;

  webVersionInfo?: WebVersionInfo;

  isAudio: boolean;

  passwordUpdatedAt?: number;

  createdAt: number;

  lastLoggedAt?: number;

  roleNames?: string;

  active: string;

  mfaEnabled?: boolean;

  authMethod?: string;

  constructor(data: RawUser) {
    super(data);
    this.id = data.id;
    this.roles = data.roles;
    this.clinic = data.clinic && new ClinicModel(data.tenant);
    this.tenant = data.tenant && new ClinicModel(data.tenant);
    this.mfaEnabled = data.mfaEnabled;
    this.authMethod = data.authMethod;
    this.mfaList = data.mfaList;
    this.status = data.status;
    this.phone = data?.phone;
    this.position = data?.position;
    this.siteId = data?.siteId;
    this.siteName = data.siteName;
    this.sites = data.sites;
    this.passwordUpdatedAt = data.passwordUpdatedAt;
    this.assignedPatient = data.assignedPatient;
    const roleNameList = this?.roles?.map((role: { name: string }) => {
      return role.name;
    });
    if (roleNameList != null) {
      if (roleNameList?.includes(RolesEnum.VivaLNKAdmin)) {
        this.isVivaLNKAdmin = true;
      }
      if (roleNameList?.includes(RolesEnum.ProgramManager)) {
        this.isProgramManager = true;
      }
      if (
        roleNameList?.includes(RolesEnum.Admin) ||
        roleNameList?.includes(RolesEnum.TenantAdmin) ||
        roleNameList?.includes(RolesEnum.SuperAdmin)
      ) {
        this.isAdmin = true;
      }
      if (roleNameList?.includes(RolesEnum.Physician)) {
        this.isPhysician = true;
      }
      if (roleNameList?.includes(RolesEnum.Technician)) {
        this.isTechnician = true;
      }
      if (roleNameList?.includes(RolesEnum.Researcher)) {
        this.isResearcher = true;
      }
    }
    this.language = data.language;
    this.signature = data.signature;
    this.signatureAuthorize = data.signatureAuthorize;
    this.webVersionInfo = data.webVersionInfo;
    this.isAudio = data.isAudio;
    this.createdAt = data.createdAt;
    this.lastLoggedAt = data.lastLoggedAt;
    this.roleNames = data.roleNames;
    this.active = data.active;
  }
}
